import { cloneDeep } from 'lodash';
import { computed, ref, type Ref } from 'vue';

import { NEWTON_PARTNERS, PartnerCodeName, type PartnerRideAllowanceTier, ADRS_PARTNERS, GVRA_PARTNERS } from '@/models/partner';
import { useProfile } from '@/use/profile';
import { usePartnerFieldConfig } from '@/use/partnerFieldConfig';
import { DEFAULT_REGISTRATION_FIELD_CONFIG } from '@/Utils/partners/partnerFieldConfig';

const partnerInfo: Ref<any> = ref(null);
const newPartnerInfo: Ref<any> = ref(null);
const partnerToViewMembers: Ref<any> = ref(null);
const partnerCodes: Ref<string[]> = ref([PartnerCodeName.CVRC, PartnerCodeName.ACRC]);

export const usePartner = () => {
  const { currentUser } = useProfile();

  const partnerId = computed(() => partnerInfo.value?.id);
  const partnerCode = computed(() => partnerInfo.value?.codeName);
  const partnerFields = usePartnerFieldConfig(() => partnerInfo.value, DEFAULT_REGISTRATION_FIELD_CONFIG);

  const partnerList = computed(() => {
    if (!currentUser.value || !currentUser.value.partners) {
      return;
    }

    return currentUser.value.partners.map((partner: any) => ({
      id: partner.id,
      name: partner.name,
      codeName: partner.codeName,
      disabled: partner.isDisabled,
      isAutoConfirmSurge: partner.isAutoConfirmSurge,
      subsidyCondition: partner.subsidyCondition,
      isApprovedAddressPartner: partner.isApprovedAddressPartner,
      isSelfServeDashboard: partner.isSelfServeDashboard,
    }));
  });

  const isApprovedAddressPartner = computed(() => Boolean(partnerInfo.value?.isApprovedAddressPartner));

  const partnerRideAllowanceTiers = computed(() => {
    return (
      partnerInfo.value?.rideAllowance?.tiers?.map((item: PartnerRideAllowanceTier) => ({
        id: item.name,
        key: item.name,
        label: item.name !== 'custom' ? `${item.allowance} rides - ${item.name}` : item.name,
      })) ?? []
    );
  });

  /** When the only configured tier is "custom", hide the dropdown and silently use custom. */
  const isOnlyCustomRideAllowanceTier = computed(() => {
    const tiers = partnerRideAllowanceTiers.value;
    return tiers.length === 1 && tiers[0].key === 'custom';
  });

  const isExternalPartnerCallerIdValid = ref(true);
  const validateExternalPartnerCallerId = (value: string) => {
    if (ADRS_PARTNERS.includes(partnerCode.value) && value && !/^\d{6}$/.test(value)) {
      isExternalPartnerCallerIdValid.value = false;
      return { valid: false, validationError: 'Must be exactly 6 digits' };
    }

    isExternalPartnerCallerIdValid.value = true;
    return { valid: true, validationError: null };
  };

  const isCvrcOrAcrc = (codeName: string) => {
    if (codeName) {
      return partnerCodes.value.some((item) => item.toLowerCase() === codeName.toLowerCase());
    }
    return false;
  };

  const isPreAuthorizationNumberValid = ref(true);
  const validatePreAuthorizationNumber = (value: string) => {
    if (ADRS_PARTNERS.includes(partnerCode.value) && value && !/^\d{7}$/.test(value)) {
      isPreAuthorizationNumberValid.value = false;
      return { valid: false, validationError: 'Must be exactly 7 digits' };
    }

    if (GVRA_PARTNERS.includes(partnerCode.value) && value && !/^\d+$/.test(value)) {
      isPreAuthorizationNumberValid.value = false;
      return { valid: false, validationError: 'Must contain one or more digits' };
    }

    isPreAuthorizationNumberValid.value = true;
    return { valid: true, validationError: null };
  };

  const isSelfServeDashboard = computed(() => Boolean(partnerInfo.value?.isSelfServeDashboard));

  // The self-serve dashboard always asks for the client's allowance tier, whatever the partner's fieldConfig says
  const isAllowanceTierVisible = computed(
    () => partnerFields.isPartnerFieldVisible('defaultRideAllowanceTier') || isSelfServeDashboard.value,
  );
  const isAllowanceTierRequired = computed(
    () => partnerFields.isPartnerFieldRequired('defaultRideAllowanceTier') || isSelfServeDashboard.value,
  );

  // The program manager comes from the fieldConfig but is part of the self-serve dashboard, so it only shows there
  const isPartnerProgramManagerVisibleAndRequired = computed(
    () => isSelfServeDashboard.value && partnerFields.isPartnerFieldVisible('partnerProgramManager'),
  );

  const setPartnerInfo = (value: any) => {
    if (value) {
      value.doNotSendMarketingMail = !value.marketingMail;
    }

    partnerInfo.value = cloneDeep(value);
  };

  const setNewPartnerInfo = (value: any) => {
    if (value) {
      value.doNotSendMarketingMail = !value.marketingMail;
    }

    newPartnerInfo.value = cloneDeep(value);
  };

  const setPartnerToViewMembers = (value: any) => {
    partnerToViewMembers.value = value;
  };

  return {
    partnerId,
    partnerCode,
    partnerInfo,
    newPartnerInfo,
    partnerToViewMembers,
    partnerList,
    ...partnerFields,
    isExternalPartnerCallerIdValid,
    validateExternalPartnerCallerId,
    isPreAuthorizationNumberValid,
    validatePreAuthorizationNumber,
    partnerRideAllowanceTiers,
    isOnlyCustomRideAllowanceTier,
    isAllowanceTierVisible,
    isAllowanceTierRequired,
    isPartnerProgramManagerVisibleAndRequired,

    isCvrcOrAcrc,
    isApprovedAddressPartner,
    isSelfServeDashboard,
    setPartnerInfo,
    setNewPartnerInfo,
    setPartnerToViewMembers,
  };
};

export const maskEstimateDataForPartner = (data: any, partnerCode: PartnerCodeName) => {
  if (!partnerCode) {
    return data;
  }

  if (NEWTON_PARTNERS.includes(partnerCode)) {
    data.gogoFeePerMin = 0;
    return data;
  }

  return data;
};
